import React from 'react';

import { callbackHooks } from '@nxte-nl/callback-hooks';
import { ErrorBoundary } from '@nxte-nl/error-boundary';
import { stateHooks } from '@nxte-nl/state-hooks';

import {
  DynamicalHeightFullScreenModalWindow,
} from '../../components/DynamicalHeightFullScreenModalWindow/DynamicalHeightFullScreenModalWindow';
import { Search } from '../../components/Search/Search';
import { SearchInput } from '../../components/SearchInput/SearchInput';
import { dataLayerHooks } from '../../hooks/dataLayerHooks/dataLayerHooks';
import { featureStateHooks } from '../../hooks/featureStateHooks/featureStateHooks';
import { messageHooks } from '../../hooks/messageHooks/messageHooks';
import { productsHooks } from '../../hooks/productsHooks/productsHooks';
import { searchHooks } from '../../hooks/searchHooks/searchHooks';
import { AppDataPopularSearchResults } from '../../types/Messages';
import { GiftcardProduct, SearchType } from '../../types/apiTypes';

export const hooks = {
  useDataLayerPush(searchText: string,
    searchResults: GiftcardProduct[] | null,
    isCompleted: boolean,
    isBlur: boolean) {
    const searchInputCallback = dataLayerHooks.useSearchInputCallback(searchText);
    const { isSearchDataLayer } = featureStateHooks.useFeatureToggles();
    React.useEffect(() => {
      if (isSearchDataLayer && isCompleted && isBlur) {
        searchInputCallback(searchResults);
      }
    },[isSearchDataLayer, isCompleted, isBlur, searchInputCallback, searchResults]);
  },

  useGetStates() {
    const [isBlur, setIsBlur] = React.useState<boolean>(false);
    const [isCompleted, setIsCompleted] = React.useState<boolean>(false);
    return {
      isBlur,
      isCompleted,
      setIsBlur,
      setIsCompleted,
    };
  },

  useSetState(setState: React.Dispatch<React.SetStateAction<boolean>>, value: boolean) {
    return React.useCallback(() => {
      setState(value);
    },[setState, value]);
  },

  useModalOffset(
    storeSwitcherHeight: number,
    additionalOffsetForModal?: number,
  ) {
    return React.useMemo(() => {
      const defaultModalOffset = 80;
      return additionalOffsetForModal
        ? defaultModalOffset + storeSwitcherHeight + additionalOffsetForModal
        : defaultModalOffset + storeSwitcherHeight;
    }, [additionalOffsetForModal, storeSwitcherHeight]);
  },

  useKeyDownAndFocus(isModalShown: boolean, showModal: () => void,
    setIsBlur: React.Dispatch<React.SetStateAction<boolean>>) {
    return React.useCallback(() => {
      setIsBlur(false);
      if (!isModalShown) {
        showModal();
      }
    }, [isModalShown, showModal, setIsBlur]);
  },

  useSearchType(isSearchV1: boolean) {
    return React.useMemo(() => {
      return isSearchV1
        ? undefined
        : [SearchType.GIFTCARD_PRODUCT];
    }, [isSearchV1]);
  },
};

export type Props = {
  headerHeight: number;
  searchInputClassName?: string;
  storeSwitcherHeight: number;
};

export const SearchContainer = React.memo(({
  storeSwitcherHeight,
  searchInputClassName,
  headerHeight,
}: Props) => {
  const popularSearchResults = messageHooks.useSubjectState(
    AppDataPopularSearchResults.PopularSearchResults,
  );

  const { isSearchV1 } = featureStateHooks.useFeatureToggles();

  const loadingPopularSearchResults =
    messageHooks.useSubjectState(AppDataPopularSearchResults.Loading);

  const [searchText, setSearchText] = React.useState<string>('');

  const {
    isBlur,
    setIsBlur,
    isCompleted,
    setIsCompleted,
  } = hooks.useGetStates();

  const searchType = hooks.useSearchType(isSearchV1);

  const {
    loading: loadingSearchResults,
    searchResults,
    isSearchInputEmpty,
  } = searchHooks.useSearchProducts(
    searchText,
    React.useCallback((searchText: string) => {
      return {
        inputText: searchText,
        type: searchType,
      };
    }, [searchType]),
    isSearchV1,
    setIsCompleted,
  );

  const {
    value: isModalShown,
    setTrue: showModal,
    setFalse: hideModal,
  } = stateHooks.useBooleanState();

  const onChangeDebounce = callbackHooks.useDebounce(setSearchText, 200);
  const onChangeSetIsCompleted = hooks.useSetState(setIsCompleted, false);
  const onChange = callbackHooks.useWrapCallbacks([onChangeDebounce, onChangeSetIsCompleted]);

  const onBlur = hooks.useSetState(setIsBlur,true);

  const handleKeyDownAndFocus = hooks.useKeyDownAndFocus(isModalShown, showModal, setIsBlur);

  hooks.useDataLayerPush(searchText, searchResults, isCompleted, isBlur);

  const modalOffset = hooks.useModalOffset(storeSwitcherHeight, headerHeight);
  const suggestions = productsHooks.useGiftcardProducts(popularSearchResults?.entities);
  return (
    <>
      <ErrorBoundary>
        <SearchInput
          className={searchInputClassName}
          onChange={onChange}
          onFocus={handleKeyDownAndFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDownAndFocus}
          hideSearchModal={hideModal}
          isSearchModalShown={isModalShown}
          variant={'white'}
          dataTau='search-input'
        />
      </ErrorBoundary>

      <ErrorBoundary>
        <DynamicalHeightFullScreenModalWindow
          isExpanded={isModalShown}
          offset={modalOffset}
          onClose={hideModal}
          headerHeight={headerHeight}
        >
          <Search
            terms={popularSearchResults?.terms}
            suggestions={suggestions as GiftcardProduct[]}
            highlightedBlocks={popularSearchResults?.highlightedBlocks}
            loadingResults={loadingSearchResults}
            loadingSuggestions={loadingPopularSearchResults}
            searchResult={searchResults}
            showSuggestions={isSearchInputEmpty}
          />
        </DynamicalHeightFullScreenModalWindow>
      </ErrorBoundary>
    </>
  );
});

SearchContainer.displayName = 'SearchContainer';
